import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Play, Pause, X, ChevronRight, Loader2, Volume2, VolumeX, Check } from 'lucide-react';
import { usePet } from '../contexts/PetContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`;

const STARS = Array.from({ length: 60 }, (_, i) => ({
  left: (i * 37) % 100, top: (i * 61) % 100, size: (i % 3) + 1, delay: (i % 7) * 0.6,
}));

export const Starfield = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
    {STARS.map((s, i) => (
      <span key={i} className="absolute rounded-full bg-white hyp-twinkle"
        style={{ left: `${s.left}%`, top: `${s.top}%`, width: s.size, height: s.size, animationDelay: `${s.delay}s` }} />
    ))}
    <style>{`
      @keyframes hypTwinkle { 0%,100% { opacity: .15 } 50% { opacity: .9 } }
      .hyp-twinkle { animation: hypTwinkle 4s ease-in-out infinite; }
      @keyframes hypBreathe { 0%,100% { transform: scale(.75) } 40% { transform: scale(1.1) } 55% { transform: scale(1.1) } }
      .hyp-breathe { animation: hypBreathe 10s ease-in-out infinite; }
    `}</style>
  </div>
);

function useTtsQueue(phases, enabled) {
  const cache = useRef({});
  const get = (i) => {
    if (!enabled || i >= phases.length) return null;
    if (!cache.current[i]) {
      cache.current[i] = axios.post(`${API}/reader/tts`,
        { text: phases[i].text, voice: 'shimmer', model: 'tts-1', speed: 0.9 },
        { withCredentials: true, responseType: 'blob' },
      ).then((r) => URL.createObjectURL(r.data));
    }
    return cache.current[i];
  };
  useEffect(() => () => {
    Object.values(cache.current).forEach((p) => p.then((u) => URL.revokeObjectURL(u)).catch(() => {}));
  }, []);
  return get;
}

function EndSteps({ session, onDone }) {
  const [steps, setSteps] = useState(session.steps.map((t) => ({ t, on: true })));
  const [saving, setSaving] = useState(false);
  const { refreshPet } = usePet();

  const finish = async (useSteps) => {
    setSaving(true);
    try {
      const chosen = useSteps ? steps.filter((s) => s.on && s.t.trim()).map((s) => s.t) : [];
      const { data } = await axios.post(`${API}/hypnos/guided/${session.id}/complete`, { steps: chosen }, { withCredentials: true });
      toast.success(data.added ? `🌙 ${data.added} pyttesteg i Idag-listan · +${data.xp} XP till husdjuret 🐾` : `🌙 Fint vilat · +${data.xp} XP 🐾`);
      refreshPet?.();
      onDone();
    } catch {
      toast.error('Kunde inte spara just nu 💜');
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto w-full" data-testid="hypnos-guided-end">
      <div className="text-center mb-5">
        <div className="text-5xl mb-2">🐶✨</div>
        <h3 className="font-['Fredoka'] text-2xl text-white">Du gjorde det, fina du</h3>
        {session.reframe && <p className="text-sm text-white/70 italic mt-2" data-testid="hypnos-guided-reframe">{session.reframe}</p>}
      </div>
      <div className="rounded-3xl p-4 bg-white/5 border border-white/10 backdrop-blur-xl">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-black mb-3">3 pyttesmå steg</div>
        <div className="space-y-2">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <button onClick={() => setSteps((p) => p.map((x, j) => (j === i ? { ...x, on: !x.on } : x)))}
                className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center border transition-colors ${s.on ? 'bg-[#C8B6FF] border-[#C8B6FF] text-[#1a1033]' : 'border-white/30 text-transparent'}`}
                data-testid={`hypnos-guided-step-toggle-${i}`}><Check className="w-4 h-4" /></button>
              <input value={s.t} onChange={(e) => setSteps((p) => p.map((x, j) => (j === i ? { ...x, t: e.target.value } : x)))}
                maxLength={120} className="flex-1 bg-transparent border-b border-white/15 text-white text-sm py-1.5 outline-none focus:border-[#C8B6FF]"
                data-testid={`hypnos-guided-step-input-${i}`} />
            </div>
          ))}
        </div>
        <button onClick={() => finish(true)} disabled={saving || !steps.some((s) => s.on)}
          className="mt-4 w-full rounded-full py-3 font-bold text-[#1a1033] bg-gradient-to-r from-[#FFC8DD] via-[#C8B6FF] to-[#A2D2FF] disabled:opacity-40 hover:scale-[1.01] transition-transform"
          data-testid="hypnos-guided-add-steps">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Lägg till i Idag-listan 🐾'}
        </button>
        <button onClick={() => finish(false)} disabled={saving} className="mt-2 w-full text-xs text-white/50 underline hover:text-white/80"
          data-testid="hypnos-guided-skip-steps">Bara vila, inga steg idag</button>
      </div>
    </div>
  );
}

export default function GuidedHypnosisPlayer({ session, voiceOn, onClose, onFinish }) {
  const phases = session.phases;
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(phases[0].duration_sec);
  const [playing, setPlaying] = useState(false);
  const [audioBusy, setAudioBusy] = useState(false);
  const [voice, setVoice] = useState(voiceOn);
  const [ended, setEnded] = useState(false);
  const audioRef = useRef(null);
  const startedIdx = useRef(-1);
  const getTts = useTtsQueue(phases, voice);
  const phase = phases[idx];

  useEffect(() => () => {
    const a = audioRef.current;
    if (a) { a.pause(); a.removeAttribute('src'); }
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (!ended) return;
    startedIdx.current = -2;
    audioRef.current?.pause();
  }, [ended]);

  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [playing]);

  useEffect(() => {
    if (!playing || !voice || ended || startedIdx.current === idx) return;
    startedIdx.current = idx;
    const a = audioRef.current || (audioRef.current = new Audio());
    setAudioBusy(true);
    getTts(idx).then((url) => {
      if (startedIdx.current !== idx) return;
      a.src = url;
      a.onended = () => setAudioBusy(false);
      a.play().catch(() => setAudioBusy(false));
      getTts(idx + 1)?.catch(() => {});
    }).catch(() => { setAudioBusy(false); toast.error('Rösten hakade upp sig — läs i lugn takt 💜'); });
  }, [idx, playing, voice]); // eslint-disable-line react-hooks/exhaustive-deps

  const goNext = () => {
    audioRef.current?.pause();
    setAudioBusy(false);
    if (idx < phases.length - 1) {
      setIdx(idx + 1);
      setRemaining(phases[idx + 1].duration_sec);
    } else {
      setPlaying(false);
      if (onFinish) onFinish(); else setEnded(true);
    }
  };

  useEffect(() => {
    if (playing && remaining <= 0 && !audioBusy) goNext();
  }, [remaining, audioBusy, playing]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => {
    const a = audioRef.current;
    if (playing) { a?.pause(); setPlaying(false); return; }
    if (!audioRef.current) audioRef.current = new Audio();
    if (a && a.src && !a.ended && audioBusy) a.play().catch(() => {});
    setPlaying(true);
  };

  const toggleVoice = () => {
    if (voice) { audioRef.current?.pause(); setAudioBusy(false); }
    else startedIdx.current = -1;
    setVoice(!voice);
  };

  const total = phases.reduce((s, p) => s + p.duration_sec, 0);
  const elapsed = phases.slice(0, idx).reduce((s, p) => s + p.duration_sec, 0) + (phase.duration_sec - remaining);
  const pct = ended ? 100 : Math.min(100, Math.round((elapsed / total) * 100));

  return (
    <div className="fixed inset-0 z-[230] overflow-auto" style={{ background: 'radial-gradient(ellipse at top, #2a1d4f 0%, #120c26 55%, #07050f 100%)' }} data-testid="hypnos-guided-player">
      <Starfield />
      <div className="relative z-10 min-h-full flex flex-col p-4 sm:p-8">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-black">Vägledd hypnos · {session.length_min} min</div>
            <div className="font-['Fredoka'] text-lg text-white truncate" data-testid="hypnos-guided-title">{session.emoji} {session.title}</div>
          </div>
          <div className="flex gap-2">
            {!ended && (
              <button onClick={toggleVoice} className="w-10 h-10 rounded-full bg-white/10 border border-white/15 text-white flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="hypnos-guided-voice-toggle" aria-label="Röst">
                {voice ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
            )}
            <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 border border-white/15 text-white flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="hypnos-guided-close" aria-label="Stäng">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="mt-4 h-1 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full transition-[width] duration-1000" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${session.accent}, #C8B6FF)` }} data-testid="hypnos-guided-progress" />
        </div>

        <div className="flex-1 flex flex-col items-center justify-center py-8">
          {ended ? <EndSteps session={session} onDone={onClose} /> : (
            <>
              <div className="relative w-44 h-44 sm:w-56 sm:h-56 flex items-center justify-center mb-6">
                <div className={`absolute inset-0 rounded-full ${playing ? 'hyp-breathe' : ''}`}
                  style={{ background: `radial-gradient(circle, ${session.accent}66 0%, ${session.accent}11 60%, transparent 70%)`, boxShadow: `0 0 80px ${session.accent}44` }} />
                <div className="relative text-center">
                  <div className="text-5xl">{phase.emoji}</div>
                  <div className="font-mono text-white/80 text-sm mt-1" data-testid="hypnos-guided-timer">{fmt(remaining)}</div>
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-black">Fas {idx + 1} av {phases.length}</div>
              <h3 className="font-['Fredoka'] text-2xl text-white mt-1" data-testid="hypnos-guided-phase-name">{phase.name}</h3>
              <p className="max-w-xl text-center text-white/75 leading-relaxed text-base mt-4 whitespace-pre-line" data-testid="hypnos-guided-text">{phase.text}</p>
              <div className="flex items-center gap-3 mt-8">
                <button onClick={toggle} className="rounded-full px-7 py-3.5 font-bold text-[#1a1033] flex items-center gap-2 hover:scale-105 transition-transform"
                  style={{ background: `linear-gradient(135deg, ${session.accent}, #C8B6FF)` }} data-testid="hypnos-guided-play">
                  {playing && audioBusy && !audioRef.current?.src ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  {playing ? 'Paus' : idx === 0 && remaining === phase.duration_sec ? 'Starta' : 'Fortsätt'}
                </button>
                <button onClick={goNext} className="rounded-full px-4 py-3.5 text-sm text-white/70 border border-white/15 flex items-center gap-1 hover:bg-white/10 transition-colors" data-testid="hypnos-guided-next">
                  Nästa <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              {playing && <div className="text-xs text-white/40 italic mt-4">Andas in när cirkeln växer · ut när den krymper</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
